import { FC } from "react";
import { clsx } from "clsx";
import { StatusDot, StatusDotColor } from "../../../design-system";
import {
    NegotiationStatus,
    ProjectAnalysisStatus,
    ProjectStatus,
    ProjectSummary,
    RfxStatus,
} from "../../../types/ProjectTypes";
import classes from "./ProjectStatusCell.module.less";

/** Label of a deal's negotiation status. */
export function negotiationStatusToString(status: NegotiationStatus): string {
    switch (status) {
        case NegotiationStatus.NOT_STARTED:
            return "Not started";
        case NegotiationStatus.IN_PROGRESS:
            return "In negotiation";
        case NegotiationStatus.READY_TO_SIGN:
            return "Ready to sign";
        case NegotiationStatus.COMPLETE:
            return "Signed";
        case NegotiationStatus.CANCELLED:
            return "Cancelled";
    }
}

/** Label of an RFx status, the RFx counterpart of a deal's negotiation status. */
export function rfxStatusToString(status: RfxStatus): string {
    switch (status) {
        case RfxStatus.NOT_STARTED:
            return "Not started";
        case RfxStatus.IN_PROGRESS:
            return "In progress";
        case RfxStatus.SELECT_VENDOR:
            return "Select vendor";
        case RfxStatus.COMPLETE:
            return "Complete";
    }
}

/** How far along the three-stage pipeline the status is: 0 to 3 filled segments. */
export type ProjectStatusStage = 0 | 1 | 2 | 3;

export interface ProjectStatusPresentation {
    label: string;
    color: StatusDotColor;
    stage: ProjectStatusStage;
}

/**
 * How a deal's status reads, or undefined while it has no order form yet.
 * The order form's negotiation status is the only input, so the hub, the
 * dashboard and the vendor pages all read the same thing. A closed deal
 * ends the pipeline either way, signed or cancelled, so the bar fills.
 */
export const dealStatusPresentation = (
    status?: NegotiationStatus
): ProjectStatusPresentation | undefined => {
    switch (status) {
        case NegotiationStatus.NOT_STARTED:
            return { label: negotiationStatusToString(status), color: "grey", stage: 0 };
        case NegotiationStatus.IN_PROGRESS:
            return { label: negotiationStatusToString(status), color: "blue", stage: 1 };
        case NegotiationStatus.READY_TO_SIGN:
            return { label: negotiationStatusToString(status), color: "orange", stage: 2 };
        case NegotiationStatus.COMPLETE:
            return { label: negotiationStatusToString(status), color: "green", stage: 3 };
        case NegotiationStatus.CANCELLED:
            return { label: negotiationStatusToString(status), color: "red", stage: 3 };
        default:
            return undefined;
    }
};

/** How an RFx's status reads, or undefined while it has no output yet. */
export const rfxStatusPresentation = (status?: RfxStatus): ProjectStatusPresentation | undefined => {
    switch (status) {
        case RfxStatus.NOT_STARTED:
            return { label: rfxStatusToString(status), color: "grey", stage: 0 };
        case RfxStatus.IN_PROGRESS:
            return { label: rfxStatusToString(status), color: "blue", stage: 1 };
        case RfxStatus.SELECT_VENDOR:
            return { label: rfxStatusToString(status), color: "orange", stage: 2 };
        case RfxStatus.COMPLETE:
            return { label: rfxStatusToString(status), color: "green", stage: 3 };
        default:
            return undefined;
    }
};

/** How a project's own status reads, for a project without a deal or RFx. */
export const projectStatusPresentation = (status: ProjectStatus): ProjectStatusPresentation | undefined => {
    switch (status) {
        case ProjectStatus.ACTIVE:
            return { label: "In progress", color: "blue", stage: 1 };
        case ProjectStatus.COMPLETED:
            return { label: "Complete", color: "green", stage: 3 };
        default:
            return undefined;
    }
};

export const statusPresentation = (
    project: ProjectSummary
): ProjectStatusPresentation | undefined => {
    // Nothing to show until an analysis completed. A draft's summary carries
    // no analysis (see toProjectSummary); a later version only ever follows
    // a completed one.
    const latest = project.latestAnalysis;
    if (!latest || (latest.version === 1 && latest.status !== ProjectAnalysisStatus.COMPLETED)) {
        return undefined;
    }
    // The deal's or RFx's status when the project has one, else its own.
    return (
        dealStatusPresentation(project.negotiationStatus) ??
        rfxStatusPresentation(project.rfxStatus) ??
        projectStatusPresentation(project.status)
    );
};

const SEGMENTS: ProjectStatusStage[] = [1, 2, 3];

/**
 * A status as a color-coded dot and label, with the pipeline progress as
 * three segments under it: none filled before the negotiation starts, one
 * while under way, two once ready, all three once complete. A dash while
 * there is no status yet.
 */
export const StatusProgressCell: FC<{ status?: ProjectStatusPresentation }> = ({ status }) => {
    if (!status) return <>–</>;
    return (
        <div className={classes.cell}>
            <StatusDot color={status.color}>{status.label}</StatusDot>
            <div className={classes.progress} aria-hidden="true">
                {SEGMENTS.map((segment) => (
                    <span
                        key={segment}
                        className={clsx(
                            classes.segment,
                            segment <= status.stage && classes["segment--filled"]
                        )}
                    />
                ))}
            </div>
        </div>
    );
};

/** The project's deal or RFx status as a StatusProgressCell. */
export const ProjectStatusCell: FC<{ project: ProjectSummary }> = ({ project }) => (
    <StatusProgressCell status={statusPresentation(project)} />
);
